import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Box,
	Snackbar,
	Alert,
	MenuItem,
	Select,
	SelectChangeEvent,
	Autocomplete,
} from '@mui/material';
import createProspect from 'api/canprospect/createProspect';
import { ProspectInterface } from 'api/canprospect/prospect';
import getProspectStatuses from 'api/canprospect/getProspectStatuses';
import useEmployeeData from '../timesheets/hooks/useEmployeeData';

interface AddProspectModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (newProspect: {
		companyName?: string;
		firstName?: string;
		lastName?: string;
		phone?: string;
		email?: string;
		address?: string;
		city?: string;
		province?: string;
		postalCode?: string;
		titleDescription?: string;
		sicCodeDescription?: string;
		consultantId?: number;
		prospectStatus: string;
		website?: string;
		prospectComment?: string;
	}) => void;
}

const AddProspectModal: React.FC<AddProspectModalProps> = ({
	open,
	onClose,
	onSave,
}) => {
	const [formValues, setFormValues] = useState({
		companyName: '',
		firstName: '',
		lastName: '',
		phone: '',
		email: '',
		address: '',
		city: '',
		province: '',
		postalCode: '',
		titleDescription: '',
		sicCodeDescription: '',
		consultantId: 0,
		prospectStatus: 'New Prospect', // Default status
		website: '',
		prospectComment: '',
	});

	const [statuses, setStatuses] = useState<string[]>([]);
	const [toastOpen, setToastOpen] = useState(false);

	const {
		employees,
		selectedEmployee,
		loadingEmployees,
		setSelectedEmployee,
	} = useEmployeeData();

	useEffect(() => {
		const fetchStatuses = async () => {
			try {
				const { statuses } = await getProspectStatuses();
				setStatuses(statuses.map((status) => status.Status));
			} catch (error) {
				console.error('Error fetching statuses:', error);
			}
		};

		if (open) {
			fetchStatuses();
		}
	}, [open]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormValues((prev) => ({ ...prev, [name]: value }));
	};

	const handleSelectChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setFormValues((prev) => ({
			...prev,
			prospectStatus: value,
		}));
	};

	const handleSave = async () => {
		try {
			const payload: ProspectInterface = {
				CompanyName: formValues.companyName || null,
				FirstName: formValues.firstName || null,
				LastName: formValues.lastName || null,
				Phone: formValues.phone || null,
				Email: formValues.email || null,
				Address: formValues.address || null,
				City: formValues.city || null,
				Province: formValues.province || null,
				PostalCode: formValues.postalCode || null,
				TitleDescription: formValues.titleDescription || null,
				SicCodeDescription: formValues.sicCodeDescription || null,
				ConsultantId: selectedEmployee
					? parseInt(selectedEmployee.Id)
					: null,
				ProspectStatus: formValues.prospectStatus, // Send string status
				Website: formValues.website || null,
				ProspectComment: formValues.prospectComment || null,
			};

			await createProspect(payload);
			onSave(formValues);
			setToastOpen(true);
			onClose();
		} catch (error) {
			console.error('Failed to create prospect:', error);
		}
	};

	const handleToastClose = () => {
		setToastOpen(false);
	};

	return (
		<>
			<Dialog open={open} onClose={onClose}>
				<DialogTitle>Add New Prospect</DialogTitle>
				<DialogContent style={{ paddingTop: '5px' }}>
					<Box display="flex" flexDirection="column" gap="1.5rem">
						{/* Row 1 */}
						<Box display="flex" gap="1rem">
							<TextField
								label="Company Name"
								name="companyName"
								value={formValues.companyName}
								onChange={handleChange}
								fullWidth
							/>
						</Box>

						{/* Row 2 */}
						<Box display="flex" gap="1rem">
							<TextField
								label="City"
								name="city"
								value={formValues.city}
								onChange={handleChange}
								fullWidth
							/>
							<TextField
								label="Industry"
								name="sicCodeDescription"
								value={formValues.sicCodeDescription}
								onChange={handleChange}
								fullWidth
							/>
						</Box>

						{/* Row 3 */}
						<Box display="flex" gap="1rem">
							<TextField
								label="Phone"
								name="phone"
								value={formValues.phone}
								onChange={handleChange}
								fullWidth
							/>
							<TextField
								label="Email"
								name="email"
								value={formValues.email}
								onChange={handleChange}
								fullWidth
							/>
						</Box>

						{/* Row 4 */}
						<Box display="flex" gap="1rem">
							<Select
								value={formValues.prospectStatus}
								onChange={handleSelectChange}
								fullWidth
							>
								{statuses.map((status) => (
									<MenuItem key={status} value={status}>
										{status}
									</MenuItem>
								))}
							</Select>
							<Autocomplete
								value={selectedEmployee}
								onChange={(event, newValue) => {
									if (newValue !== null) {
										setSelectedEmployee(newValue);
									}
								}}
								loading={loadingEmployees}
								fullWidth
								options={employees}
								getOptionLabel={(option) => option.FullName}
								renderInput={(params) => (
									<TextField {...params} label="Employee" />
								)}
								disabled={loadingEmployees}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions
					style={{ marginBottom: '1rem', marginRight: '10px' }}
				>
					<Button
						onClick={handleSave}
						variant={'contained'}
						color="primary"
					>
						Save
					</Button>
					<Button onClick={onClose} color="secondary">
						Cancel
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={toastOpen}
				autoHideDuration={3000}
				onClose={handleToastClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleToastClose}
					severity="success"
					sx={{ width: '100%' }}
				>
					Prospect added successfully!
				</Alert>
			</Snackbar>
		</>
	);
};

export default AddProspectModal;
